import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useService } from './ServiceContext';
import { SearchAddressResult } from '../system/search';
import { RecommendationResponse } from '../system/recommendations';
import { PropertyField } from '../system/field';
import { useMetadata } from './MetadataContext';
import { useAuth } from './AuthContext';
import { extractCreateParams, isPersonalAssistant } from './util/createParams';
import {
  ListingProvider,
  ListingResource,
  useFieldUpdater,
} from '@brightmls/nextgen.lae.flc';

export enum DraftListingSection {
  LocationSearch,
  PropertyType,
  ListingAssistant,
  Participants,
  CreateDraft,
}

interface DraftListingContextType {
  assistant?: RecommendationResponse;
  fetchSuggestions: (addressResult: SearchAddressResult) => Promise<void>;
  show: (draftListingSection: DraftListingSection) => boolean;
  setLocation: (result: SearchAddressResult) => void;
  clearListing: () => void;
  location?: SearchAddressResult;
  suggestionsError?: Error;
  isLoadingSuggestions: boolean;
}

const DraftListingContext = createContext<DraftListingContextType | undefined>(
  undefined,
);

export const DraftListingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [assistant, setAssistant] = useState<
    RecommendationResponse | undefined
  >();
  const [location, setLocationState] = useState<
    SearchAddressResult | undefined
  >();
  const [suggestionsError, setSuggestionsError] = useState<Error | undefined>();
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const fetchingRef = useRef(false);
  const urlParamsProcessedRef = useRef(false);

  const { getSuggestions, findAgent } = useService();
  const { session } = useAuth();
  const { lookup } = useMetadata();

  const updateFields = useFieldUpdater(ListingResource.Property);

  // Process URL parameters on initial load
  useEffect(() => {
    if (urlParamsProcessedRef.current) return;

    const processUrlParams = async () => {
      const params = extractCreateParams();

      //  Handle online application parameter
      if (params.allowOnlineApplication) {
        updateFields({
          [PropertyField.ALLOW_ONLINE_APPLICATION_YN]: true,
        });
      }

      // Handle property type parameter
      if (params.propertyType) {
        // Validate property type against lookup values
        const propertyTypes = lookup('Property_Type') || [];
        const validPropertyType = propertyTypes.find(
          (option) => option.LookupValue === params.propertyType,
        );

        if (validPropertyType) {
          updateFields({
            [PropertyField.PROPERTY_TYPE]: validPropertyType.LookupValue,
          });
        }
      }

      // Handle list agent parameter
      if (params.listAgent) {
        try {
          const agents = await findAgent(params.listAgent, true);

          if (agents && agents.length > 0) {
            const agent = agents[0];

            // If this is a personal assistant, they can't be set as the list agent
            if (
              isPersonalAssistant(session) &&
              agent.MemberMlsId === session?.user.memberMlsId
            ) {
              console.log(
                'Personal assistants cannot be set as the list agent',
              );
              return;
            }

            // Update list agent fields
            updateFields({
              [PropertyField.LIST_AGENT_KEY]: agent.MemberKey,
              [PropertyField.LIST_AGENT_FULL_NAME]: agent.MemberFullName,
              [PropertyField.LIST_AGENT_EMAIL]: agent.MemberEmail,
              [PropertyField.LIST_AGENT_MLS_ID]: agent.MemberMlsId,
              [PropertyField.LIST_AGENT_CELL_PHONE]: agent.MemberMobilePhone,
              [PropertyField.LIST_AGENT_DIRECT_PHONE]: agent.MemberDirectPhone,
              [PropertyField.LIST_OFFICE_KEY]: agent.OfficeKey,
              [PropertyField.LIST_OFFICE_NAME]: agent.OfficeName,
              [PropertyField.LIST_OFFICE_PHONE]: agent.MemberOfficePhone,
              [PropertyField.LIST_OFFICE_MLS_ID]: agent.OfficeMlsId,
              [PropertyField.LIST_AGENT_STATE_LICENSE_NUMBER]:
                agent.MemberStateLicense,
            });
          }
        } catch (error) {
          console.error('Error fetching agent data from URL parameter:', error);
        }
      }

      urlParamsProcessedRef.current = true;
    };

    processUrlParams();
  }, [session, lookup, findAgent]);

  const fetchSuggestions = async (addressResult: SearchAddressResult) => {
    // Reset error state at the start of each fetch
    setSuggestionsError(undefined);
    setIsLoadingSuggestions(true);
    fetchingRef.current = true;

    try {
      if (addressResult.TaxRecord) {
        updateFields({
          [PropertyField.TAX_ID]: `${addressResult.TaxRecord.TaxId}`,
          [PropertyField.PARCEL_ID]: `${addressResult.TaxRecord.ParcelId}`,
        });
      }
      const suggestions = await getSuggestions(addressResult);

      // Only update state if this is still the most recent fetch
      if (fetchingRef.current) {
        setAssistant(suggestions);
      }
    } catch (error) {
      // Only update error state if this is still the most recent fetch
      if (fetchingRef.current) {
        setSuggestionsError(
          error instanceof Error
            ? error
            : new Error('Failed to fetch suggestions'),
        );
        setAssistant(undefined);
      }
    } finally {
      if (fetchingRef.current) {
        setIsLoadingSuggestions(false);
        fetchingRef.current = false;
      }
    }
  };

  const setLocation = (result: SearchAddressResult) => {
    setLocationState(result);
  };

  const clearListing = () => {
    updateFields({
      [PropertyField.UNPARSED_ADDRESS]: null,
      [PropertyField.STREET_NUMBER]: null,
      [PropertyField.STREET_NAME]: null,
      [PropertyField.CITY]: null,
      [PropertyField.STATE_OR_PROVINCE]: null,
      [PropertyField.POSTAL_CODE]: null,
      [PropertyField.COUNTY]: null,
      [PropertyField.FULL_STREET_ADDRESS]: null,
      [PropertyField.LATITUDE]: null,
      [PropertyField.LONGITUDE]: null,
    });

    setLocationState(undefined);
    setAssistant(undefined);
    setSuggestionsError(undefined);
    setIsLoadingSuggestions(false);
    fetchingRef.current = false;
  };

  useEffect(() => {
    if (location) {
      fetchSuggestions(location);
    }

    return () => {
      // Clean up by marking any in-progress fetch as stale
      fetchingRef.current = false;
    };
  }, [location]);

  const show = (draftListingSection: DraftListingSection) => {
    switch (draftListingSection) {
      case DraftListingSection.PropertyType:
      case DraftListingSection.Participants:
      case DraftListingSection.ListingAssistant:
        return !!location;
      case DraftListingSection.LocationSearch:
        return !!assistant;
      case DraftListingSection.CreateDraft:
        return true;
      default:
        return false;
    }
  };

  return (
    <DraftListingContext.Provider
      value={{
        assistant,
        fetchSuggestions,
        show,
        setLocation,
        location,
        clearListing,
        suggestionsError,
        isLoadingSuggestions,
      }}
    >
      {children}
    </DraftListingContext.Provider>
  );
};

export const useDraftListing = () => {
  const context = useContext(DraftListingContext);
  if (context === undefined) {
    throw new Error(
      'useDraftListing must be used within a DraftListingProvider',
    );
  }
  return context;
};

export const DraftListing: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const metadata = useMetadata();
  const { saveListing } = useService();

  const handleSave = async (listing: any) => {
    const action = listing.key ? 'UPDATE' : 'CREATE';

    try {
      const result = await saveListing(action, listing.key, {
        Property: listing.Property,
        OpenHouse: listing.OpenHouse,
        Media: listing.Media,
        Unit: listing.Unit,
        Room: listing.Room,
        GreenVerification: listing.GreenVerification,
      });

      return result;
    } catch (error) {
      console.error('Save listing error:', error);
      return {
        error:
          error instanceof Error ? error.message : 'Failed to save listing',
      };
    }
  };

  return (
    <ListingProvider
      onSave={handleSave}
      rules={metadata?.rules() || []}
      validationContext={{
        hasPermission: () => true,
        locale: metadata?.locale(),
      }}
    >
      <DraftListingProvider>{children}</DraftListingProvider>
    </ListingProvider>
  );
};
